import { useState, useEffect, useCallback } from "react";

const G = "#0077B5";

const PLAN_INFO = {
  presence: { label: "Presence", monthly: 99, desc: "Consistent posts from your topics" },
  authority: { label: "Authority", monthly: 199, desc: "Topics plus your plug" },
  pro: { label: "Pro", monthly: 299, desc: "Expert commentary in your field" },
};

export default function RetentionWaterfall({ user, API_BASE, onClose, onCancelled, setToast }) {
  const plan = (user?.plan || "free").toLowerCase();
  const isComped = user?.comped && !user?.stripe_sub_id;
  const isMemberNotAdmin = user?.is_workspace_member && !user?.is_workspace_admin;
  const isWorkspaceAdmin = user?.is_workspace_admin;

  const getInitialStep = () => {
    if (plan === "free") return "free";
    if (plan === "presence") return 2;
    return 1; // authority, authority_pro, team, team_pro
  };

  const [step, setStep] = useState(getInitialStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState("");
  const [optOutPitch, setOptOutPitch] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // 2-click confirmation: auto-revert after 5 seconds
  useEffect(() => {
    if (!confirmDelete) return;
    const t = setTimeout(() => setConfirmDelete(false), 5000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  // Escape key closes modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const callCancel = async (body) => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/cancel-account`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": localStorage.getItem("nectar_st"),
        },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (r.ok) return data;
      setError(data.error || "Something went wrong.");
      return null;
    } catch {
      setError("Network error. Please try again.");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Downgrade to lower paid tier
  const handleDowngrade = async (targetPlan) => {
    const result = await callCancel({ action: "downgrade_paid_tier", target_plan: targetPlan });
    if (result) {
      if (setToast) setToast(`Downgraded to ${PLAN_INFO[targetPlan]?.label || targetPlan}.`);
      onCancelled?.();
    }
  };

  // Step 2: Downgrade to free
  const handleDowngradeFree = async () => {
    const result = await callCancel({ action: "downgrade_free", opt_out_pitch: optOutPitch });
    if (result) {
      if (setToast) setToast("You'll move to the Free plan at the end of your billing cycle.");
      onCancelled?.();
    }
  };

  // Step 3 / free step: Soft delete
  const handleSoftDelete = async () => {
    const result = await callCancel({
      action: "soft_delete",
      opt_out_pitch: optOutPitch,
      reason: reason.trim() || undefined,
    });
    if (result) {
      onCancelled?.();
    }
  };

  // Free step: opt-out pitch only
  const handleOptOutOnly = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/user/opt-out-pitch`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": localStorage.getItem("nectar_st"),
        },
      });
      if (r.ok) {
        if (setToast) setToast("Pitch emails stopped. You won't hear from us about upgrading.");
        onCancelled?.();
      } else {
        const data = await r.json().catch(() => ({}));
        setError(data.error || "Something went wrong.");
      }
    } catch {
      setError("Network error. Please try again.");
    }
    setLoading(false);
  };

  // Free step: upgrade via portal
  const handleUpgrade = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/billing/portal-session`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Session-Token": localStorage.getItem("nectar_st"),
        },
      });
      const data = await r.json();
      if (r.ok && data.portal_url) {
        window.location.href = data.portal_url;
      } else {
        setError(data.error || "Could not open billing portal.");
      }
    } catch {
      setError("Network error. Please try again.");
    }
    setLoading(false);
  };

  const style = {
    overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000 },
    modal: { background: "#fff", borderRadius: 16, maxWidth: 480, width: "90%", padding: "32px 28px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", maxHeight: "90vh", overflowY: "auto" },
    heading: { fontFamily: "'Playfair Display',Georgia,serif", fontSize: 22, fontWeight: 700, color: "#1A1A1A", marginBottom: 8 },
    body: { fontSize: 15, color: "#666", lineHeight: 1.7, marginBottom: 20 },
    error: { background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", color: "#B91C1C", fontSize: 13, marginBottom: 14 },
    warning: { background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 8, padding: "10px 14px", color: "#92400E", fontSize: 13, marginBottom: 14 },
    btnPrimary: { width: "100%", background: G, color: "#fff", border: "none", borderRadius: 8, padding: "14px", fontSize: 15, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, marginBottom: 10 },
    btnGreen: { width: "100%", background: "#22C55E", color: "#fff", border: "none", borderRadius: 8, padding: "14px", fontSize: 15, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, marginBottom: 10 },
    btnRed: { width: "100%", background: "#EF4444", color: "#fff", border: "none", borderRadius: 8, padding: "14px", fontSize: 15, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1, marginBottom: 10 },
    btnOutline: { width: "100%", background: "transparent", color: "#666", border: "1px solid #D1D5DB", borderRadius: 8, padding: "12px", fontSize: 14, cursor: "pointer", marginBottom: 10 },
    btnLink: { background: "none", border: "none", color: "#999", fontSize: 13, cursor: "pointer", textDecoration: "underline", marginTop: 4 },
    textarea: { width: "100%", border: "1px solid #D1D5DB", borderRadius: 8, padding: "10px 12px", fontSize: 14, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", resize: "vertical", minHeight: 60, marginBottom: 14, boxSizing: "border-box" },
    checkbox: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#555", marginBottom: 16, cursor: "pointer" },
    planCard: { border: `1px solid ${G}`, borderRadius: 10, padding: "14px 18px", marginBottom: 10, cursor: loading ? "not-allowed" : "pointer", transition: "background 0.15s" },
    confirmRow: { display: "flex", gap: 10 },
  };

  // Blocked states
  if (isComped) {
    return (
      <div style={style.overlay} onClick={onClose}>
        <div style={style.modal} onClick={e => e.stopPropagation()}>
          <div style={style.heading}>Complimentary account</div>
          <div style={style.body}>Your account has been granted free access. To make changes, please contact <strong>operator@bhive.ca</strong>.</div>
          <button style={style.btnOutline} onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  if (isMemberNotAdmin) {
    return (
      <div style={style.overlay} onClick={onClose}>
        <div style={style.modal} onClick={e => e.stopPropagation()}>
          <div style={style.heading}>Managed account</div>
          <div style={style.body}>Your subscription is managed by your workspace admin. To cancel, leave the workspace first from the Teams section in Settings.</div>
          <button style={style.btnOutline} onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  // Soft delete confirmation (shared by paid Step 3 and free step)
  const renderSoftDelete = () => (
    <>
      <div style={style.heading}>We're sorry to see you go</div>
      <div style={style.body}>
        Your data will be preserved forever. You can reactivate anytime.
      </div>

      {isWorkspaceAdmin && (
        <div style={style.warning}>
          You are a workspace admin. Cancelling will suspend your workspace and notify all members.
        </div>
      )}

      <textarea
        style={style.textarea}
        placeholder="What could we have done better? (optional)"
        value={reason}
        onChange={e => setReason(e.target.value)}
        maxLength={255}
      />

      <label style={style.checkbox} onClick={() => setOptOutPitch(!optOutPitch)}>
        <input type="checkbox" checked={optOutPitch} onChange={() => setOptOutPitch(!optOutPitch)} />
        Don't email me about returning
      </label>

      {error && <div style={style.error}>{error}</div>}

      {!confirmDelete ? (
        <button style={style.btnRed} onClick={() => setConfirmDelete(true)} disabled={loading}>
          Delete my account
        </button>
      ) : (
        <div style={style.confirmRow}>
          <button
            style={{ ...style.btnRed, flex: 1, background: "#B91C1C" }}
            onClick={handleSoftDelete}
            disabled={loading}
          >
            {loading ? "Cancelling\u2026" : "Confirm \u2014 delete forever?"}
          </button>
          <button
            style={{ ...style.btnOutline, flex: 1, marginBottom: 0 }}
            onClick={() => setConfirmDelete(false)}
          >
            Cancel
          </button>
        </div>
      )}

      <button style={style.btnLink} onClick={onClose}>Keep my account</button>
    </>
  );

  return (
    <div style={style.overlay} onClick={onClose}>
      <div style={style.modal} onClick={e => e.stopPropagation()}>

        {/* ── Step 1: Downgrade to lower paid tier (Authority+ users) ── */}
        {step === 1 && (
          <>
            <div style={style.heading}>Before you go&hellip;</div>
            <div style={style.body}>Would you like to keep posting on a lighter plan?</div>

            {error && <div style={style.error}>{error}</div>}

            {(plan === "pro_team" || plan === "authority_team" || plan === "agency") && (
              <div
                style={style.planCard}
                onClick={() => !loading && handleDowngrade("pro")}
              >
                <div style={{ fontSize: 16, fontWeight: 600 }}>Pro</div>
                <div style={{ fontSize: 13, color: "#666" }}>$299/mo &middot; Expert commentary in your field</div>
              </div>
            )}

            {(plan === "pro_team" || plan === "authority_team" || plan === "agency" || plan === "pro") && (
              <div
                style={style.planCard}
                onClick={() => !loading && handleDowngrade("authority")}
              >
                <div style={{ fontSize: 16, fontWeight: 600 }}>Authority</div>
                <div style={{ fontSize: 13, color: "#666" }}>$199/mo &middot; Topics plus your plug</div>
              </div>
            )}

            <div
              style={style.planCard}
              onClick={() => !loading && handleDowngrade("presence")}
            >
              <div style={{ fontSize: 16, fontWeight: 600 }}>Presence</div>
              <div style={{ fontSize: 13, color: "#666" }}>$99/mo &middot; Consistent posts from your topics</div>
            </div>

            {loading && <div style={{ fontSize: 13, color: "#999", textAlign: "center", marginBottom: 10 }}>Updating plan&hellip;</div>}

            <button style={style.btnLink} onClick={() => setStep(2)}>No thanks, I want to cancel</button>
          </>
        )}

        {/* ── Step 2: Downgrade to free (any paid user) ── */}
        {step === 2 && (
          <>
            <div style={style.heading}>How about the Free plan?</div>
            <div style={style.body}>
              You'll still get regular posts at no cost. Your content and settings stay safe.
            </div>

            {error && <div style={style.error}>{error}</div>}

            <label style={style.checkbox} onClick={() => setOptOutPitch(!optOutPitch)}>
              <input type="checkbox" checked={optOutPitch} onChange={() => setOptOutPitch(!optOutPitch)} />
              Also stop plan upgrade emails
            </label>

            <button style={style.btnGreen} onClick={handleDowngradeFree} disabled={loading}>
              {loading ? "Processing\u2026" : "Switch to Free plan"}
            </button>

            <button style={style.btnLink} onClick={() => setStep(3)}>No, I want to fully cancel</button>
          </>
        )}

        {/* ── Step 3: Soft delete (paid users who refused downgrade) ── */}
        {step === 3 && renderSoftDelete()}

        {/* ── Free user retention step ── */}
        {step === "free" && (
          <>
            <div style={style.heading}>What would you like to do?</div>
            <div style={style.body}>You're on the Free plan. Here are your options:</div>

            {error && <div style={style.error}>{error}</div>}

            <button style={style.btnPrimary} onClick={handleUpgrade} disabled={loading}>
              {loading ? "Opening\u2026" : "Upgrade to keep posting more"}
            </button>

            <button style={style.btnOutline} onClick={handleOptOutOnly} disabled={loading}>
              Just stop the pitch emails
            </button>

            <button style={style.btnLink} onClick={() => setStep(3)}>Delete my account</button>
          </>
        )}
      </div>
    </div>
  );
}
